/**
 * Utility functions for validating neural network models
 */

/**
 * Analyzes a model to check for connection compatibility issues
 * 
 * @param {Array} layers - Array of layer objects in sequential order
 * @param {boolean} [isSubmitting=false] - Whether the model is being submitted (stricter validation)
 * @returns {Object} - Validation result with isValid flag and issues array
 */
export const validateModel = (layers, isSubmitting = false) => {
  // During initial editing, no layers is valid - only show error on submit
  if (!layers || layers.length === 0) {
    return {
      isValid: !isSubmitting, // Valid during editing, invalid on submit
      issues: isSubmitting ? [{ message: "Model doesn't have any layers" }] : []
    };
  }
  
  // For models with just one layer, only show error on submit
  if (layers.length === 1) {
    return {
      isValid: true, // Changed to always be valid since we now allow single layer models
      issues: []
    };
  }

  const issues = [];
  let currentShape = null;
  let previousLayer = null;

  // Keep track of the model's input shape
  for (let i = 0; i < layers.length; i++) {
    const layer = layers[i];
    const nextLayer = i < layers.length - 1 ? layers[i + 1] : null;
    
    // Validate each layer based on type and expected shape
    const layerIssues = validateLayerConnection(previousLayer, layer, nextLayer, currentShape);
    
    if (layerIssues.length > 0) {
      issues.push(...layerIssues.map(issue => ({
        ...issue,
        layerIndex: i,
        layerType: layer.type
      })));
    }
    
    // Update shape for next layer to consume
    currentShape = getOutputShape(layer, currentShape);
    previousLayer = layer;
  }

  return {
    isValid: issues.length === 0,
    issues
  };
};

/**
 * Validates a specific layer's connection compatibility
 * 
 * @param {Object} previousLayer - The previous layer in the model
 * @param {Object} currentLayer - The current layer being validated
 * @param {Object} nextLayer - The next layer in the model
 * @param {Array|null} inputShape - The expected input shape for the current layer
 * @returns {Array} - Array of issues found
 */
const validateLayerConnection = (previousLayer, currentLayer, nextLayer, inputShape) => {
  const issues = [];
  const layerType = currentLayer.type;

  // Validate based on layer type
  switch (layerType) {
    case 'Conv1D':
    case 'Conv2D':
      // Conv layers need proper input dimensions
      if (previousLayer && previousLayer.type === 'Flatten') {
        issues.push({
          message: `${layerType} cannot be connected after Flatten`,
          suggestion: `Move ${layerType} before Flatten or use Reshape first`
        });
      }
      break;
      
    case 'Dense':
      // Dense layers need flattened input if coming from convolutional layers
      if (previousLayer && (previousLayer.type === 'Conv1D' || previousLayer.type === 'Conv2D' || 
          previousLayer.type === 'MaxPooling1D' || previousLayer.type === 'AveragePooling1D') && 
          !previousLayer.flatten_output) {
        issues.push({
          message: `Dense layer needs flattened input from ${previousLayer.type}`,
          suggestion: "Add a Flatten layer before Dense or set flatten_output=true on the previous layer"
        });
      }
      break;
      
    case 'LSTM':
    case 'GRU':
    case 'RNN':
      // Check if return_sequences is compatible with the next layer
      if (currentLayer.return_sequences === false && nextLayer && 
          (nextLayer.type === 'LSTM' || nextLayer.type === 'GRU' || nextLayer.type === 'RNN')) {
        issues.push({
          message: `${layerType} with return_sequences=false cannot be connected to another recurrent layer`,
          suggestion: `Set return_sequences=true on this ${layerType} layer`
        });
      }
      
      // Dense layer after LSTM/GRU only works without return_sequences or with TimeDistributed
      if (currentLayer.return_sequences !== false && nextLayer && nextLayer.type === 'Dense' && 
          (!nextLayer.is_time_distributed)) {
        issues.push({
          message: `${layerType} with return_sequences=true cannot connect directly to Dense`,
          suggestion: "Set return_sequences=false or use TimeDistributed Dense"
        });
      }
      break;
      
    case 'TimeDistributed':
      // TimeDistributed needs a 3D input shape
      if (previousLayer && 
          !(previousLayer.type === 'LSTM' || previousLayer.type === 'GRU' || previousLayer.type === 'RNN') && 
          !(previousLayer.return_sequences)) {
        issues.push({
          message: "TimeDistributed requires a 3D tensor input",
          suggestion: "Use after a recurrent layer with return_sequences=true"
        });
      }
      break;
      
    case 'Bidirectional':
      // Bidirectional needs a recurrent layer
      if (!currentLayer.layer_type || 
          !(currentLayer.layer_type === 'LSTM' || currentLayer.layer_type === 'GRU' || currentLayer.layer_type === 'RNN')) {
        issues.push({
          message: "Bidirectional wrapper needs a recurrent layer type",
          suggestion: "Set layer_type to LSTM, GRU, or RNN"
        });
      }
      break;
      
    case 'Reshape':
      // Validate reshape dimensions
      if (!currentLayer.target_shape || !Array.isArray(currentLayer.target_shape)) {
        issues.push({
          message: "Reshape layer needs a valid target_shape array",
          suggestion: "Provide a target_shape array like [28, 28]"
        });
      }
      break;
  }

  // Special validation for transition between different dimensional layers
  if (previousLayer) {
    // Transition from 1D to 2D or vice versa needs reshape
    if ((is1DLayer(previousLayer) && is2DLayer(currentLayer)) || 
        (is2DLayer(previousLayer) && is1DLayer(currentLayer))) {
      if (layerType !== 'Reshape' && layerType !== 'Flatten') {
        issues.push({
          message: `Dimension mismatch between ${previousLayer.type} and ${layerType}`,
          suggestion: "Add a Reshape or Flatten layer between these layers"
        });
      }
    }
    
    // Check for recurrent layer connections - modified for default return_sequences=true
    if (isRecurrentLayer(previousLayer) && 
        previousLayer.return_sequences === false && // explicit check for false
        isRecurrentLayer(currentLayer)) {
      issues.push({
        message: `Recurrent layer with return_sequences=false cannot connect to another recurrent layer`,
        suggestion: "Set return_sequences=true on the previous recurrent layer"
      });
    }
  }

  return issues;
};

/**
 * Estimates output shape of a layer based on its configuration and input shape
 * 
 * @param {Object} layer - Layer configuration
 * @param {Array|null} inputShape - Input shape to the layer
 * @returns {Array|null} - Estimated output shape
 */
const getOutputShape = (layer, inputShape) => {
  // Default to passing through the shape if we can't determine
  if (!layer || !inputShape) return inputShape;
  
  switch (layer.type) {
    case 'Dense':
      // Dense outputs [batch_size, units]
      return [null, layer.units];
      
    case 'Flatten':
      // Flatten collapses all dimensions except batch to 1D
      return [null, -1]; // -1 means flattened dimension
      
    case 'Reshape':
      // Reshape changes to target shape
      return [null, ...(layer.target_shape || [])];
      
    case 'LSTM':
    case 'GRU':
    case 'RNN':
      if (layer.return_sequences) {
        // With return_sequences, we keep the time dimension
        return [null, null, layer.units]; // [batch, time, units]
      } else {
        // Without return_sequences, we lose the time dimension
        return [null, layer.units]; // [batch, units]
      }
      
    case 'Conv1D':
      // [batch, time, filters]
      return [null, null, layer.filters];
      
    case 'Conv2D':
      // [batch, height, width, filters]
      return [null, null, null, layer.filters];
      
    case 'MaxPooling1D':
    case 'AveragePooling1D':
      // Pooling reduces the time dimension but keeps channels
      return inputShape; // Simplified - we should calculate the reduced dimension
      
    default:
      return inputShape;
  }
};

/**
 * Helper function to check if a layer operates on 1D data
 */
const is1DLayer = (layer) => {
  return ['Conv1D', 'MaxPooling1D', 'AveragePooling1D', 'LSTM', 'GRU', 'RNN'].includes(layer.type);
};

/**
 * Helper function to check if a layer operates on 2D data
 */
const is2DLayer = (layer) => {
  return ['Conv2D', 'MaxPooling2D', 'AveragePooling2D'].includes(layer.type);
};

/**
 * Helper function to check if a layer is a recurrent layer
 */
const isRecurrentLayer = (layer) => {
  return ['LSTM', 'GRU', 'RNN', 'Bidirectional'].includes(layer.type);
};

/**
 * Gets human-readable explanation of the issue for display to the user
 * 
 * @param {Array} issues - Array of validation issues
 * @returns {String} - Formatted explanation message
 */
export const getValidationMessage = (issues) => {
  if (!issues || issues.length === 0) return null;
  
  let message = "Model contains compatibility issues:\n";
  
  issues.forEach((issue, index) => {
    const layerInfo = issue.layerType 
      ? `Layer ${issue.layerIndex + 1} (${issue.layerType}): ` 
      : '';
    
    message += `${index + 1}. ${layerInfo}${issue.message}\n`;
    if (issue.suggestion) {
      message += `   Suggestion: ${issue.suggestion}\n`;
    }
  });
  
  return message;
}; 